import createLogger from "../../utils/logger.util";
import { ModelId } from "../../types/ModelId";
import OptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { Base, Language } from "..";

const logger = createLogger({ name: "Currency" });

function toSettings(settings?: Partial<Currency.Settings>) {
	if (!settings?.code || !settings.name || !settings.symbol) return undefined;

	return {
		code: settings.code,
		name: settings.name,
		symbol: settings.symbol,
	};
}

class Currency extends Base {
	public static fromResponse(data: any): Currency.Model {
		return {
			id: data.id,
			name: data.name,
			settings: data.settings,
			root: data?.root,
		};
	}

	public static toRequest(
		model?: Partial<Currency.Model> | Currency.Payload,
	): Currency.Payload | undefined {
		if (!model) return undefined;

		const settings = toSettings(model.settings);

		if (!model.name || !settings) return undefined;

		return {
			name: model.name,
			settings,
		};
	}

	public static async getAll() {
		const res = await this.request(
			(prpc) => prpc.theirsModel.currency.getAll(),
			{ silent: false, error: true },
		);
		return res?.items?.map(this.fromResponse);
	}

	public static async create(model: Currency.Payload) {
		try {
			const payload = this.toRequest(model);

			if (!payload) return false;

			const res = await this.request(
				(prpc) => prpc.theirsModel.currency.create(payload),
				{ silent: false, error: true },
			);

			return res ? this.fromResponse(res) : false;
		} catch (err: any) {
			logger.error("[Currency] Error create:", err);
			return false;
		}
	}

	public static async update(model: Currency.Modified) {
		try {
			const payload = this.toRequest(model);

			if (!payload) return false;

			const res = await this.request(
				(prpc) => prpc.theirsModel.currency.update(model.id, payload),
				{ silent: false, error: true },
			);

			return res ? this.fromResponse(res) : false;
		} catch (err: any) {
			logger.error("[Currency] Error update:", err);
			return false;
		}
	}

	public static async remove(id: number | number[]) {
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.currency.remove(id),
				{
					silent: false,
					error: true,
				},
			);
			return res;
		} catch (err: any) {
			logger.error("[Currency] Error remove:", err);
			return false;
		}
	}
}

declare namespace Currency {
	interface Payload {
		name: Name;
		settings: Pick<Settings, "code" | "name" | "symbol">;
	}

	interface Modified extends Payload {
		id: ModelId;
	}

	interface Model {
		readonly id?: ModelId;
		readonly name: Name;
		readonly settings: Settings;
		readonly root: boolean;
	}

	type Name = Record<Language, string>;

	interface Settings {
		code: string;
		name: string;
		symbol: string;
	}

	interface SubscribeOptions extends OptionsBase<Currency.Model> {}
}

export default Currency;
